import React, { ReactNode, useEffect, useState } from 'react';
import { Button, Form, Input, Pagination, Popconfirm, Select, Spin, Table } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { ColumnsType } from 'antd/lib/table';
import { useAppSelector } from 'app/store';
import { viewerModel } from 'entities/viewer';
import { dateForAdminsTable } from 'shared/helpers/utils';
import { useScrollUpPage } from 'shared/hooks';
import { RolesUser } from 'shared/enums';
import { useDeleteAdminMutation, useLazyGetAdminRolesQuery, useLazyGetAdminsQuery } from 'app/services/edpApiV1/admins';
import { AdminsDataPrepared } from 'shared/types/admins.types';
import { useNotifications } from 'shared/hooks/use-notifications';
import { ORGANIZATION_ID } from 'shared/constants/admins';
import { UpdateAdminModal } from '../updateAdmin';
import OrganizationInput from '../addAdmin/organizationInput/OrganizationInput';
import style from './index.module.scss';

const { Search } = Input;
export const ROLES_COUNT = 100;
export enum AdminSearchFields {
  FIO = 'fio',
  EMAIL = 'email',
  ROLE = 'role',
}

const initialValues = {
  selectOptions: 'fio',
};

const AdminsBlocked = () => {
  const [currentOrganization, setCurrentOrganization] = useState<string | null>(null);
  const [isEditingModalOpen, setIsEditingModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [currentAdmin, setCurrentAdmin] = useState<AdminsDataPrepared | null>(null);
  const [optionsSearch, setOptionsSearch] = useState<AdminSearchFields>(AdminSearchFields.FIO);
  const [form] = useForm();
  const [getAdminsRequest, { data: admins, isLoading, isFetching }] = useLazyGetAdminsQuery();
  const [getAdminsRoles, { data: adminRoles }] = useLazyGetAdminRolesQuery();
  const [
    unblockAdminQuery,
    { data: deleteResult, isError: isDeleteError, isSuccess: isDeleteSuccess, error: deleteError, reset: resetDelete },
  ] = useDeleteAdminMutation();

  useEffect(() => {
    getAdminsRoles({ page: 1, size: ROLES_COUNT });
  }, []);

  useEffect(() => {
    getAdminsRequest({ page: currentPage, size: currentSize, active: false });
  }, [currentPage, currentSize]);

  useNotifications(
    isDeleteSuccess,
    isDeleteError,
    `Администратор автивирован`,
    (deleteError as any)?.data?.message,
    resetDelete,
  );

  const { role } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const onClickToRow = (admin: AdminsDataPrepared) => {
    setCurrentAdmin(admin);
    setIsEditingModalOpen(true);
  };

  const deleteAdmin = (id: number) => {
    unblockAdminQuery({ employeeId: id, active: true });
  };

  const columns: ColumnsType<AdminsDataPrepared> = [
    {
      title: 'id',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },

    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },

    {
      title: 'Действие',
      dataIndex: 'action',
      key: 'action',
      render: (_, record: AdminsDataPrepared) => (
        <div className={style.controlsBtnGroup} onClick={e => e.stopPropagation()}>
          {role === RolesUser.HRDirector && (
            <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => deleteAdmin(+record.id)}>
              <Button type="primary">Активировать</Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const onSearch = (value: string) => {
    const query: Record<string, string | number> = {};
    if (currentOrganization) {
      query[ORGANIZATION_ID] = currentOrganization;
    }
    if (value) {
      query[optionsSearch] = value;
    }
    getAdminsRequest({ page: 1, size: currentSize, ...query, active: false });
    setCurrentPage(1);
  };

  const handlePagination = (value: number, pageSize: number) => {
    setCurrentSize(pageSize);
    setCurrentPage(value);
  };

  const onSelectOptionsSearch = (value: AdminSearchFields) => {
    setOptionsSearch(value);
  };

  const organizationChanged = (organizationId: string) => {
    setCurrentOrganization(organizationId);
  };

  const onOrganizationsClear = () => {
    setCurrentOrganization(null);
  };

  const pickSearchType = (): ReactNode => {
    let placeholder = '';
    switch (optionsSearch) {
      case AdminSearchFields.FIO:
        placeholder = 'Поиск сотрудника по ФИО';
        break;
      case AdminSearchFields.ROLE:
        placeholder = 'Поиск сотрудника по роли';
        return (
          <Form.Item name={AdminSearchFields.FIO} className={style.searchInput}>
            <Select
              placeholder={placeholder}
              onSelect={onSearch}
              options={adminRoles?.data.map(role => ({ label: role.name, value: role.id }))}
              style={{ width: '100%' }}
            ></Select>
          </Form.Item>
        );
      case AdminSearchFields.EMAIL:
        placeholder = 'Поиск сотрудника по email';
        break;
    }

    return (
      <Form.Item name={AdminSearchFields.FIO} className={style.searchInput}>
        <Search placeholder={placeholder} allowClear onSearch={onSearch} />
      </Form.Item>
    );
  };

  useScrollUpPage(currentPage);

  return (
    <>
      <Form form={form} className={style.searchWrap} initialValues={initialValues}>
        <OrganizationInput
          organizationChanged={organizationChanged}
          isSearchField={true}
          onClear={onOrganizationsClear}
        />
        <Form.Item name="selectOptions">
          <Select placeholder="Параметры поиска" className={style.selectOrganizations} onSelect={onSelectOptionsSearch}>
            <Select.Option key={AdminSearchFields.FIO} value={AdminSearchFields.FIO}>
              Поиск по ФИО
            </Select.Option>
            <Select.Option key={AdminSearchFields.ROLE} value={AdminSearchFields.ROLE}>
              Поиск по роли
            </Select.Option>
            <Select.Option key={AdminSearchFields.EMAIL} value={AdminSearchFields.EMAIL}>
              Поиск по email
            </Select.Option>
          </Select>
        </Form.Item>

        {pickSearchType()}
      </Form>
      <UpdateAdminModal admin={currentAdmin} isModalOpen={isEditingModalOpen} setIsModalOpen={setIsEditingModalOpen} />
      <Spin spinning={isLoading || isFetching}>
        <div className={style.tableWrap}>
          <Table
            scroll={{ x: true }}
            dataSource={dateForAdminsTable(admins?.data || [])}
            columns={columns}
            pagination={false}
            rowClassName={style.row}
            onRow={(record, rowIndex) => {
              return {
                onClick: () => {
                  if (onClickToRow) {
                    onClickToRow(record);
                  }
                },
              };
            }}
          />
          <div className={style.paginationWrap}>
            <Pagination
              showSizeChanger
              defaultCurrent={currentSize}
              current={currentPage}
              pageSize={currentSize}
              total={admins?.pagination.total}
              onChange={(value: number, pageSize: number) => handlePagination(value, pageSize)}
            />
          </div>
        </div>
      </Spin>
    </>
  );
};

export default AdminsBlocked;
